/**
 * Best-effort English → Tamil-script transliteration ("Tanglish").
 *
 * Goal: when a Tamil-speaking user sees an English word that isn't in our
 * translation dictionary (e.g. "Photographer"), render it phonetically in
 * Tamil script (e.g. "ஃபொடொக்ராஃபர்") so the UI never breaks immersion.
 *
 * Limitations:
 *   • Rule-based, not perfect — best for short business / category words.
 *   • For high-traffic terms, prefer a hand-curated entry in the
 *     `translations` map in LanguageContext.js so the user sees the real
 *     Tamil word ("புகைப்படக்காரர்") instead of the transliteration.
 *   • Numbers, punctuation, and non-Latin characters pass through unchanged.
 */

// Trigraphs / digraphs that map to a single Tamil sound. Order matters
// because we greedy-match the longest prefix first.
const MULTIGRAPHS = [
  ['tion', 'ஷன்'],
  ['sion', 'ஷன்'],
  ['ph', 'ப'],
  ['ch', 'ச'],
  ['sh', 'ஷ'],
  ['th', 'த'],
  ['kh', 'க'],
  ['gh', 'க'],
  ['ck', 'க்'],
  ['ng', 'ங்'],
];

// Standalone vowels (no preceding consonant).
const STAND_VOWEL = {
  a: 'அ', aa: 'ஆ',
  i: 'இ', ee: 'ஈ',
  u: 'உ', oo: 'ஊ',
  e: 'எ', ai: 'ஐ',
  o: 'ஒ', ou: 'ஔ',
};

// Matras — vowel marks attached to a preceding consonant. We default
// single 'o' and 'e' to their LONG forms (ோ, ே) because English business
// words read more naturally that way ("photo" → போடோ, not பொடொ).
const MATRA = {
  a: '', aa: 'ா',
  i: 'ி', ee: 'ீ',
  u: 'ு', oo: 'ூ',
  e: 'ே', ai: 'ை',
  o: 'ோ', ou: 'ௌ',
};

// Single-letter consonants with their inherent-'a' Tamil form.
const CONS = {
  b: 'ப', c: 'க', d: 'ட', f: 'ஃப', g: 'க', h: 'ஹ', j: 'ஜ',
  k: 'க', l: 'ல', m: 'ம', n: 'ன', p: 'ப', q: 'க', r: 'ர',
  s: 'ஸ', t: 'ட', v: 'வ', w: 'வ', x: 'க்ஸ்', y: 'ய', z: 'ஸ',
};

const VOWEL_LETTERS = 'aeiou';

const isLatin = (s) => /[a-zA-Z]/.test(s);

const matchLongVowel = (s, i) => {
  const two = s.slice(i, i + 2);
  if (MATRA[two] !== undefined && two.length === 2) return two;
  return null;
};

const transliterateWord = (rawWord) => {
  if (!rawWord) return rawWord;
  const word = rawWord.toLowerCase();
  if (!isLatin(word)) return rawWord; // nothing to transliterate

  let out = '';
  let i = 0;
  while (i < word.length) {
    const rest = word.slice(i);

    // 1) Try multigraph consonant clusters first.
    let multi = null;
    for (const [pat, repl] of MULTIGRAPHS) {
      if (rest.startsWith(pat)) { multi = [pat, repl]; break; }
    }
    if (multi) {
      const [pat, replConsonant] = multi;
      // If the multigraph is a standalone cluster like 'ck' or 'ng',
      // it's already self-contained — emit and continue.
      if (replConsonant.endsWith('்') || replConsonant.endsWith('ன்')) {
        out += replConsonant;
        i += pat.length;
        continue;
      }
      // Otherwise look ahead for a vowel and build a syllable.
      i += pat.length;
      const next = word[i];
      if (next && VOWEL_LETTERS.includes(next)) {
        const longV = matchLongVowel(word, i);
        if (longV) { out += replConsonant + MATRA[longV]; i += 2; continue; }
        out += replConsonant + (MATRA[next] !== undefined ? MATRA[next] : '');
        i += 1;
        continue;
      }
      out += replConsonant + '்';
      continue;
    }

    const c = word[i];

    // 2) Standalone vowel (not preceded by consonant in this slot).
    if (VOWEL_LETTERS.includes(c)) {
      const longV = matchLongVowel(word, i);
      if (longV && STAND_VOWEL[longV]) { out += STAND_VOWEL[longV]; i += 2; continue; }
      out += STAND_VOWEL[c] || c;
      i += 1;
      continue;
    }

    // 3) Single consonant.
    if (CONS[c]) {
      i += 1;
      const next = word[i];
      if (next && VOWEL_LETTERS.includes(next)) {
        const longV = matchLongVowel(word, i);
        if (longV) { out += CONS[c] + MATRA[longV]; i += 2; continue; }
        out += CONS[c] + (MATRA[next] !== undefined ? MATRA[next] : '');
        i += 1;
        continue;
      }
      out += CONS[c] + '்';
      continue;
    }

    // 4) Unknown character — pass through.
    out += c;
    i += 1;
  }
  return out;
};

/**
 * Transliterate every English-letter run inside a string while keeping
 * separators (spaces, punctuation, digits) intact.
 *
 *   "Wedding Photographer 2026" → "வெட்டிங் ஃபொடொக்ராஃபர் 2026"
 */
export const transliterateToTamil = (text) => {
  if (text === null || text === undefined) return text;
  const str = String(text);
  if (!isLatin(str)) return str; // already Tamil / Hindi / digits-only
  // Tokenise into Latin runs vs everything-else so we only convert words.
  return str.replace(/[A-Za-z]+/g, (word) => transliterateWord(word));
};

export default transliterateToTamil;
